import { useState, useEffect, useCallback, useMemo } from 'react';
import { timesheetService } from 'api/services/timesheetService';
import type {
  ID, PaginatedResponse, Timesheet,
  CreateTimesheetRequest, UpdateTimesheetRequest, TimesheetSearchRequest,
  ReopenTimesheetRequest,
} from 'api/types';

const EMPTY_PAGINATION = {
  currentPage: 1, pageSize: 25, totalPages: 0, totalItems: 0,
  hasNextPage: false, hasPreviousPage: false,
};

export const useTimesheets = (params?: TimesheetSearchRequest) => {
  const [data, setData] = useState<Timesheet[]>([]);
  const [pagination, setPagination] = useState<PaginatedResponse<Timesheet>['pagination']>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const fetchTimesheets = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await timesheetService.getAll(params);
      setData(r.data); setPagination(r.pagination);
    } catch (err) { setError(err as Error); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchTimesheets(); }, [fetchTimesheets]);
  return { data, pagination, loading, error, refetch: fetchTimesheets };
};

export const useTimesheet = (id: ID | null | undefined) => {
  const [data, setData] = useState<Timesheet | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const fetch = useCallback(async () => {
    if (!id) { setData(null); return; }
    setLoading(true); setError(null);
    try { const r = await timesheetService.getById(id); setData(r.data); }
    catch (err) { setError(err as Error); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, error, refetch: fetch };
};

/** Fetch a specific employee's timesheet for a given week start date */
export const useTimesheetByWeek = (employeeId: string | null, weekStartDate: string | null) => {
  const [data, setData] = useState<Timesheet | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const key = `${employeeId}-${weekStartDate}`;

  const fetch = useCallback(async () => {
    if (!employeeId || !weekStartDate) { setData(null); return; }
    setLoading(true); setError(null);
    try {
      const r = await timesheetService.getByEmployeeWeek(employeeId, weekStartDate);
      setData(r.data);
    } catch (err: any) {
      // 404 = no timesheet for this week yet — not an error, just empty
      if (err?.response?.status === 404) {
        setData(null);
      } else {
        setError(err as Error);
      }
    } finally { setLoading(false); }
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, error, refetch: fetch };
};

export const useCreateTimesheet = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const createTimesheet = async (data: CreateTimesheetRequest, options?: { onSuccess?: (item: Timesheet) => void; onError?: (e: Error) => void }) => {
    setLoading(true); setError(null);
    try { const r = await timesheetService.create(data); options?.onSuccess?.(r.data); return r.data; }
    catch (err) { const e = err as Error; setError(e); options?.onError?.(e); throw e; }
    finally { setLoading(false); }
  };
  return { createTimesheet, loading, error };
};

export const useUpdateTimesheet = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const updateTimesheet = async (id: ID, data: UpdateTimesheetRequest, options?: { onSuccess?: (item: Timesheet) => void; onError?: (e: Error) => void }) => {
    setLoading(true); setError(null);
    try { const r = await timesheetService.update(id, data); options?.onSuccess?.(r.data); return r.data; }
    catch (err) { const e = err as Error; setError(e); options?.onError?.(e); throw e; }
    finally { setLoading(false); }
  };
  return { updateTimesheet, loading, error };
};

export const useDeleteTimesheet = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const deleteTimesheet = async (id: ID, options?: { onSuccess?: () => void; onError?: (e: Error) => void }) => {
    setLoading(true); setError(null);
    try { await timesheetService.delete(id); options?.onSuccess?.(); }
    catch (err) { const e = err as Error; setError(e); options?.onError?.(e); throw e; }
    finally { setLoading(false); }
  };
  return { deleteTimesheet, loading, error };
};

export const useReopenTimesheet = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const reopenTimesheet = async (
    id: ID,
    data: ReopenTimesheetRequest,
    options?: { onSuccess?: (item: Timesheet) => void; onError?: (e: Error) => void },
  ) => {
    setLoading(true); setError(null);
    try { const r = await timesheetService.reopen(id, data); options?.onSuccess?.(r.data); return r.data; }
    catch (err) { const e = err as Error; setError(e); options?.onError?.(e); throw e; }
    finally { setLoading(false); }
  };
  return { reopenTimesheet, loading, error };
};
